import { Clock } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

/**
 * OKS-13 — Shared brand-lifecycle primitive.
 *
 * Visual chip for the OKS-12 BE6 N2 `brand.live` lifecycle. Renders ONLY
 * when `live === false` per UX Spec — `live: true` is the default state
 * and gets no badge (keeps amber Pending the salient signal across the
 * Brands list). Same component used on:
 *
 *   - `Agency/Brands/{Index,Show}` (this ticket — OKS-13)
 *   - `Admin/Brands/{Index,Show}` (this ticket — F7 backport)
 *   - any future tenant-list surface that needs the "pending activation"
 *     signal post-Path-D revoke
 *
 * QA contract spec asserts the same exported symbol is imported by both
 * variants (admin + agency) — fork prevention.
 *
 * Currently a single-state badge (only renders when pending activation).
 * Designed for forward-compat: if the lifecycle expands (`archived`,
 * `deleted`, etc.) the prop becomes a discriminated union and additional
 * branches plug in here without touching consumers.
 */

interface BrandLiveBadgeProps {
  /**
   * The brand's `live` flag. Component is a no-op (returns `null`) when
   * `true` — caller doesn't need to wrap in a conditional. Pass through
   * directly: `<BrandLiveBadge live={brand.live} />`.
   */
  live: boolean
  className?: string
  /** Override the locked `data-testid`. Defaults to `agency-brand-live-badge` per OKS-13 QA contract. */
  testId?: string
}

/**
 * Renders a brand "Pending activation" chip when `live === false`;
 * returns `null` otherwise.
 *
 * `data-state` carries the raw `live` discriminator (`"pending_activation"`
 * for `live: false`). Vitest contract asserts the badge is keyed off
 * `props.live` directly — never derived from any related field (e.g. team
 * size or invitation presence).
 *
 * Default `data-testid="agency-brand-live-badge"` matches the OKS-13
 * QA-locked contract. F7 backport on `Admin/Brands/{Index,Show}` overrides
 * via `testId="admin-brand-live-badge"` — same component, different testid
 * namespace per surface.
 */
export function BrandLiveBadge({
  live,
  className,
  testId = "agency-brand-live-badge",
}: BrandLiveBadgeProps) {
  const { t } = useTranslation()

  // Default state — `live: true` is the silent norm. UX rule: no badge,
  // no chrome, no SR announcement. Caller can pass through unconditionally.
  if (live) return null

  return (
    <Badge
      variant="secondary"
      role="status"
      aria-label={t("brand_live.pending_activation")}
      data-testid={testId}
      data-state="pending_activation"
      className={cn(
        "gap-1.5 border-amber-300/60 bg-amber-100 text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/30 dark:text-amber-200",
        className,
      )}
    >
      <Clock aria-hidden="true" />
      <span>{t("brand_live.pending_activation")}</span>
    </Badge>
  )
}
